/* Copy da lista de publishers: governança (verificar/suspender/banir), filtros de status/chaves
   e o diálogo de confirmação de ação. O verbo da ação (`ACTION_VERB`) continua vindo de
   `publisher-status-map.ts` sem tradução — fora do escopo desta migração — por isso as
   mensagens do diálogo são funções que recebem o verbo já resolvido. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface PublishersListContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly provisionButton: string;
  readonly governanceAlert: string;
  readonly searchLabel: string;
  readonly searchPlaceholder: string;
  readonly statusFilterAriaLabel: string;
  readonly keyFilterAriaLabel: string;
  readonly statusFilterLabels: Readonly<
    Record<'all' | 'pending' | 'verified' | 'suspended' | 'banned', string>
  >;
  readonly keyFilterLabels: Readonly<Record<'all' | 'pendingKeys' | 'revokedKeys', string>>;
  readonly backlogLabel: string;
  readonly loadError: string;
  readonly confirmDialogTitle: string;
  readonly confirmReasonHint: string;
  readonly bulkConfirmMessage: (verb: string, count: number) => string;
  readonly singleConfirmMessage: (verb: string, name: string) => string;
  readonly reasonLabel: string;
  readonly reasonPlaceholder: string;
  readonly cancelButton: string;
  readonly confirmButton: string;
  readonly verifySuccess: (signedKeyCount: number) => string;
  readonly verifyError: string;
  readonly actionAppliedSuccess: (verb: string) => string;
  readonly actionAppliedError: (verb: string) => string;
  readonly bulkActionSuccess: (count: number) => string;
  readonly bulkActionPartial: (okCount: number, failedCount: number) => string;
  readonly bulkActionError: string;
}

const PUBLISHERS_LIST_CONTENT: Bilingual<PublishersListContent> = {
  'pt-BR': {
    pageTitle: 'Publishers',
    pageDescription: 'Governança dos publishers — verificação de chaves, suspensão e banimento.',
    provisionButton: 'Provisionar publisher',
    governanceAlert:
      'Verificar root-assina as chaves do publisher (habilita packs community + fastpath). ' +
      'Suspender é reversível; banir é terminal (só super_admin) — ambos derrubam o fastpath.',
    searchLabel: 'Buscar publishers',
    searchPlaceholder: 'Buscar por nome ou slug…',
    statusFilterAriaLabel: 'Filtrar por status',
    keyFilterAriaLabel: 'Filtrar por estado das chaves',
    statusFilterLabels: {
      all: 'Todos',
      pending: 'Pendentes',
      verified: 'Verificados',
      suspended: 'Suspensos',
      banned: 'Banidos',
    },
    keyFilterLabels: {
      all: 'Todos',
      pendingKeys: 'Pendentes de root-sign',
      revokedKeys: 'Chaves revogadas',
    },
    backlogLabel: 'Com backlog',
    loadError: 'Falha ao carregar os publishers.',
    confirmDialogTitle: 'Confirmar ação',
    confirmReasonHint: 'Informe um motivo (fica na trilha de auditoria).',
    bulkConfirmMessage: (verb, count) => `${verb} ${count} publisher(s) selecionado(s).`,
    singleConfirmMessage: (verb, name) => `${verb} “${name}”.`,
    reasonLabel: 'Motivo',
    reasonPlaceholder: 'Ex.: denúncia de abuso confirmada',
    cancelButton: 'Cancelar',
    confirmButton: 'Confirmar',
    verifySuccess: (signedKeyCount) =>
      `Publisher verificado (${signedKeyCount} chave(s) assinada(s)).`,
    verifyError: 'Falha ao verificar o publisher.',
    actionAppliedSuccess: (verb) => `Publisher: ${verb} aplicado.`,
    actionAppliedError: (verb) => `Falha ao ${verb} o publisher.`,
    bulkActionSuccess: (count) => `Ação aplicada a ${count} publisher(s).`,
    bulkActionPartial: (okCount, failedCount) => `${okCount} ok, ${failedCount} com falha.`,
    bulkActionError: 'Falha ao aplicar a ação em lote.',
  },
  'en-US': {
    pageTitle: 'Publishers',
    pageDescription: 'Publisher governance — key verification, suspension, and banning.',
    provisionButton: 'Provision publisher',
    governanceAlert:
      "Verifying root-signs the publisher's keys (enables community packs + fastpath). " +
      'Suspending is reversible; banning is terminal (super_admin only) — both take down the fastpath.',
    searchLabel: 'Search publishers',
    searchPlaceholder: 'Search by name or slug…',
    statusFilterAriaLabel: 'Filter by status',
    keyFilterAriaLabel: 'Filter by key state',
    statusFilterLabels: {
      all: 'All',
      pending: 'Pending',
      verified: 'Verified',
      suspended: 'Suspended',
      banned: 'Banned',
    },
    keyFilterLabels: {
      all: 'All',
      pendingKeys: 'Pending root-sign',
      revokedKeys: 'Revoked keys',
    },
    backlogLabel: 'With backlog',
    loadError: 'Failed to load publishers.',
    confirmDialogTitle: 'Confirm action',
    confirmReasonHint: 'Provide a reason (goes into the audit trail).',
    bulkConfirmMessage: (verb, count) => `${verb} ${count} selected publisher(s).`,
    singleConfirmMessage: (verb, name) => `${verb} "${name}".`,
    reasonLabel: 'Reason',
    reasonPlaceholder: 'E.g.: confirmed abuse report',
    cancelButton: 'Cancel',
    confirmButton: 'Confirm',
    verifySuccess: (signedKeyCount) => `Publisher verified (${signedKeyCount} key(s) signed).`,
    verifyError: 'Failed to verify the publisher.',
    actionAppliedSuccess: (verb) => `Publisher: ${verb} applied.`,
    actionAppliedError: (verb) => `Failed to ${verb} the publisher.`,
    bulkActionSuccess: (count) => `Action applied to ${count} publisher(s).`,
    bulkActionPartial: (okCount, failedCount) => `${okCount} ok, ${failedCount} failed.`,
    bulkActionError: 'Failed to apply the bulk action.',
  },
};

export const usePublishersListContent = (): PublishersListContent =>
  useBilingual(PUBLISHERS_LIST_CONTENT);
